import { useState, type ReactNode } from 'react';
import { AuthError, appleSignInUrl, googleSignInUrl } from '@orborix/auth-client';
import { useAuth } from '../../auth/AuthProvider.js';
import { useViewNavigate } from '../../hooks/useViewNavigate.js';
import {
  nativeSocialProviders,
  signInWithNativeProvider,
  webSocialProviders,
  type SocialProvider,
} from '../../lib/native/socialLogin.js';
import { isNative, nativePlatform } from '../../lib/platform.js';
import { track } from '../../lib/analytics.js';
import { socialLoginErrorMessage, type SocialSurface } from './socialLoginCopy.js';

/**
 * Shared auth UI primitives, ported from
 * claude-design/orborix-speak/project/auth-screens.jsx.
 *
 * Single responsive tree (mobile body layout) — the desktop two-column hero
 * is dropped because the AppShell caps the column at ~440px (phone frame).
 * The field is EMAIL (not "usuario") even where the design labelled it user.
 */

// ─── Inline icons ───────────────────────────────────────────────────

interface IconProps {
  size?: number;
}

export function AuthIconMail({ size = 18 }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <path d="M4 7 L 12 13 L 20 7" />
    </svg>
  );
}

export function AuthIconUser({ size = 18 }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="9" r="4" />
      <path d="M4 21 c 1 -4, 4 -6, 8 -6 s 7 2, 8 6" />
    </svg>
  );
}

export function AuthIconLock({ size = 18 }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
      strokeLinecap="round"
    >
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11 V 8 a 4 4 0 0 1 8 0 V 11" />
    </svg>
  );
}

interface EyeProps extends IconProps {
  off?: boolean;
}

export function AuthIconEye({ size = 18, off = false }: EyeProps) {
  if (off) {
    return (
      <svg
        viewBox="0 0 24 24"
        width={size}
        height={size}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 3 L 21 21" />
        <path d="M10.5 6.2 a 9.7 9.7 0 0 1 1.5 -0.2 c 5 0 9 4 10 6 a 13 13 0 0 1 -3 3.7" />
        <path d="M6.5 7.5 C 4.5 9, 3 11, 2 12 c 1 2, 5 6, 10 6 a 9 9 0 0 0 3.5 -0.7" />
        <path d="M9.9 9.9 a 3 3 0 0 0 4.2 4.2" />
      </svg>
    );
  }
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 12 c 1 -2, 5 -6, 10 -6 s 9 4, 10 6 c -1 2, -5 6, -10 6 s -9 -4, -10 -6 Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export function AuthIconArrow({ size = 16 }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12 H 19" />
      <path d="M13 6 L 19 12 L 13 18" />
    </svg>
  );
}

export function AuthIconCheck({ size = 12 }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="3.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12 L 10 17 L 19 7" />
    </svg>
  );
}

export function AuthIconGoogle({ size = 18 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size}>
      <path
        d="M21.6 12.23c0-.74-.07-1.45-.2-2.13H12v4.03h5.4a4.62 4.62 0 0 1-2 3.04v2.52h3.23c1.9-1.74 2.97-4.3 2.97-7.46z"
        fill="#4285F4"
      />
      <path
        d="M12 22c2.7 0 4.96-.9 6.62-2.43l-3.23-2.52c-.9.6-2.04.96-3.39.96-2.6 0-4.8-1.76-5.6-4.12H3.07v2.6A10 10 0 0 0 12 22z"
        fill="#34A853"
      />
      <path d="M6.4 13.9a6 6 0 0 1 0-3.8V7.5H3.07a10 10 0 0 0 0 9l3.33-2.6z" fill="#FBBC05" />
      <path
        d="M12 5.98c1.47 0 2.79.51 3.83 1.5l2.86-2.86A10 10 0 0 0 12 2a10 10 0 0 0-8.93 5.5l3.33 2.6c.8-2.36 3-4.12 5.6-4.12z"
        fill="#EA4335"
      />
    </svg>
  );
}

export function AuthIconApple({ size = 18 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
      <path d="M16.4 12.6c0-2.5 2-3.7 2.1-3.8-1.1-1.7-3-1.9-3.6-1.9-1.5-.2-3 .9-3.8.9-.8 0-2-.9-3.3-.9-1.7 0-3.3 1-4.2 2.6-1.8 3.1-.5 7.7 1.3 10.2.9 1.2 1.9 2.6 3.2 2.5 1.3 0 1.8-.8 3.3-.8 1.6 0 2 .8 3.4.8 1.4 0 2.3-1.2 3.1-2.5.7-.9 1.2-1.9 1.5-3-1.3-.5-2.9-1.7-2.9-4.1zm-2.5-7.5c.7-.8 1.2-2 1-3.1-1 0-2.2.7-2.9 1.5-.7.7-1.3 1.9-1.1 3 1.1.1 2.3-.5 3-1.4z" />
    </svg>
  );
}

// ─── Logo mark ──────────────────────────────────────────────────────

export function AuthBrandRow() {
  return (
    <div className="auth-brand">
      <img className="auth-brand-lockup" src="/brand/orborix-speak-logo.svg" alt="orborix speak" />
    </div>
  );
}

// ─── Input field ────────────────────────────────────────────────────

export type FieldState = 'error' | 'success' | null;
export type HelpState = 'error' | 'ok' | null;

interface AuthFieldProps {
  label?: string;
  hint?: string;
  icon?: ReactNode;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoComplete?: string;
  help?: string | null;
  helpState?: HelpState;
  state?: FieldState;
  onEnter?: () => void;
}

export function AuthField({
  label,
  hint,
  icon,
  type = 'text',
  value,
  onChange,
  placeholder,
  autoComplete,
  help,
  helpState,
  state,
  onEnter,
}: AuthFieldProps) {
  const [focused, setFocused] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const isPassword = type === 'password';
  const actualType = isPassword && showPw ? 'text' : type;

  let cls = 'auth-input';
  if (focused) cls += ' is-focused';
  if (state === 'error') cls += ' is-error';
  else if (state === 'success') cls += ' is-success';

  return (
    <div className="auth-field">
      {label && (
        <div className="auth-field-label">
          <span>{label}</span>
          {hint && <span className="hint">{hint}</span>}
        </div>
      )}
      <div className={cls}>
        {icon && <span className="auth-input-icon">{icon}</span>}
        <input
          type={actualType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && onEnter) onEnter();
          }}
          placeholder={placeholder}
          autoComplete={autoComplete}
          spellCheck={false}
          autoCapitalize="none"
        />
        {isPassword && (
          <button
            type="button"
            className="auth-eye"
            onClick={() => setShowPw((s) => !s)}
            aria-label={showPw ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          >
            <AuthIconEye off={showPw} />
          </button>
        )}
      </div>
      {help && <div className={`auth-help ${helpState ?? ''}`}>{help}</div>}
    </div>
  );
}

// ─── Password strength ──────────────────────────────────────────────

interface Strength {
  score: number;
  label: string;
  state: string;
}

const STRENGTH_LABELS = ['', 'DÉBIL', 'MEJORABLE', 'BUENA', 'FUERTE'];
const STRENGTH_STATES = ['', 'weak', 'ok', 'good', 'strong'];

function scorePassword(p: string): Strength {
  if (!p) return { score: 0, label: '', state: '' };
  let s = 0;
  if (p.length >= 8) s++;
  if (p.length >= 12) s++;
  if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s++;
  if (/\d/.test(p) && /[^A-Za-z0-9]/.test(p)) s++;
  return { score: s, label: STRENGTH_LABELS[s] ?? '', state: STRENGTH_STATES[s] ?? '' };
}

interface PasswordStrengthProps {
  value: string;
}

export function PasswordStrength({ value }: PasswordStrengthProps) {
  if (!value) return null;
  const { score, label, state } = scorePassword(value);
  return (
    <div className={`auth-strength ${state}`}>
      <div className={`auth-strength-bars s-${score}`}>
        <span />
        <span />
        <span />
        <span />
      </div>
      <span className="auth-strength-label">{label}</span>
    </div>
  );
}

// ─── Social row (Google / Apple) ───────────────────────────────────

/**
 * Which provider the web redirect was started with. The web callback comes back
 * as `/login?error=oauth_<code>` without naming the provider, and a full-page
 * navigation loses React state, so the provider rides in sessionStorage.
 */
const WEB_OAUTH_PROVIDER_KEY = 'speak.auth.oauthProvider';

function rememberWebOauthProvider(provider: SocialProvider): void {
  try {
    sessionStorage.setItem(WEB_OAUTH_PROVIDER_KEY, provider);
  } catch {
    // No storage: the error banner falls back to the provider-neutral copy.
  }
}

/** The provider of the last web redirect (read once), or `null` if unknown. */
export function takeWebOauthProvider(): SocialProvider | null {
  try {
    const value = sessionStorage.getItem(WEB_OAUTH_PROVIDER_KEY);
    sessionStorage.removeItem(WEB_OAUTH_PROVIDER_KEY);
    return value === 'google' || value === 'apple' ? value : null;
  } catch {
    return null;
  }
}

function socialSurface(): SocialSurface {
  if (!isNative) return 'web';
  return nativePlatform() === 'ios' ? 'ios' : 'android';
}

const PROVIDER_ICON: Record<SocialProvider, ReactNode> = {
  google: <AuthIconGoogle />,
  apple: <AuthIconApple />,
};

// Apple's HIG asks a custom Sign in with Apple button to read "Continue with
// Apple" (or be logo-only); Google's label follows the same pattern.
const PROVIDER_LABEL: Record<SocialProvider, string> = {
  google: 'Continuar con Google',
  apple: 'Continuar con Apple',
};

interface AuthSocialRowProps {
  /** Shows (string) or clears (`null`) the screen's error banner. */
  onError: (message: string | null) => void;
  /** Another step of the screen is busy (e.g. sending the code). */
  disabled?: boolean;
}

/**
 * Google and Apple sign-in (plan 50). In the app: the native flows of
 * `lib/native/socialLogin.ts`, in platform order (iOS Apple first, Android
 * Google first), each only when its build config exists. On the web: full-page
 * redirects (`/api/auth/google`, `/api/auth/apple` behind
 * `VITE_APPLE_OAUTH_ENABLED`). The web links never show inside the app.
 *
 * A native sign-in ends exactly like `otpVerify`: token persisted by the
 * auth-client, user set, `isNewUser` → onboarding (/welcome), else /home.
 */
export function AuthSocialRow({ onError, disabled = false }: AuthSocialRowProps) {
  const { completeSignIn } = useAuth();
  const navigate = useViewNavigate();
  const [pending, setPending] = useState<SocialProvider | null>(null);
  const providers = isNative ? nativeSocialProviders() : webSocialProviders();
  if (providers.length === 0) return null;

  const onNative = async (provider: SocialProvider) => {
    if (pending || disabled) return;
    onError(null);
    setPending(provider);
    try {
      const result = await signInWithNativeProvider(provider);
      // The user closed the OS sheet / the Custom Tab: intent, not an error.
      if (!result) return;
      completeSignIn(result);
      track('sign_in_completed', { method: provider, new_user: result.isNewUser });
      void navigate(result.isNewUser ? '/welcome' : '/home', { replace: true });
    } catch (err) {
      if (err instanceof AuthError) {
        onError(socialLoginErrorMessage(err.code, provider, socialSurface()));
      } else {
        // OS / plugin failure (NativeSocialLoginError) or a bug: generic copy,
        // the detail goes to the console (Sentry breadcrumbs, logcat).
        console.error('[AuthSocialRow] native sign-in failed', provider, err);
        onError(socialLoginErrorMessage('native_failure', provider, socialSurface()));
      }
    } finally {
      setPending(null);
    }
  };

  const onWeb = (provider: SocialProvider) => {
    rememberWebOauthProvider(provider);
    // Full top-level redirect (no fetch): OAuth needs a page navigation. Both
    // URLs carry the native language speak signs users up with (mandatory).
    window.location.href = provider === 'google' ? googleSignInUrl() : appleSignInUrl();
  };

  return (
    <>
      <div className="auth-divider">
        <span>o</span>
      </div>
      <div className="auth-socials">
        {providers.map((provider) => (
          <button
            key={provider}
            type="button"
            className={`auth-social-btn auth-social-btn--${provider}`}
            disabled={disabled || pending !== null}
            aria-busy={pending === provider}
            onClick={() => (isNative ? void onNative(provider) : onWeb(provider))}
          >
            {PROVIDER_ICON[provider]} {PROVIDER_LABEL[provider]}
          </button>
        ))}
      </div>
    </>
  );
}

// ─── Email validation (client-side, mirrors backend zod email) ──────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(email: string): boolean {
  return EMAIL_RE.test(email.trim());
}
